import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { BadgeCheck, BookOpenCheck, Brain, Save, Target } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/services/api";

function ProfileHeader({ user }) {
  return (
    <section className="profile-hero" data-testid="profile-hero-section">
      <div>
        <p className="section-kicker" data-testid="profile-page-kicker">perfil do aluno</p>
        <h1 className="font-display text-4xl font-black sm:text-5xl lg:text-6xl" data-testid="profile-page-heading">{user.name}</h1>
        <p className="mt-3 text-base font-bold text-slate-600 md:text-lg" data-testid="profile-email-text">{user.email}</p>
      </div>
      <div className="profile-badge" data-testid="profile-level-badge"><BadgeCheck size={22} />{user.level}</div>
    </section>
  );
}

function ProgressCard({ progress }) {
  return (
    <article className="neo-card surface-yellow p-5" data-testid="profile-progress-card">
          <Target size={28} />
          <h2 className="mt-3 font-display text-3xl font-black" data-testid="profile-progress-title">Resumo individual</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2" data-testid="profile-progress-grid">
            <div className="mini-metric" data-testid="profile-progress-sessions">{progress?.sessions_completed || 0} sessões</div>
            <div className="mini-metric" data-testid="profile-progress-minutes">{progress?.minutes_practiced || 0} min</div>
            <div className="mini-metric" data-testid="profile-progress-score">{progress?.average_score || 0}% média</div>
            <div className="mini-metric" data-testid="profile-progress-streak">{progress?.streak_days || 0} dias</div>
          </div>
          <p className="mt-4 text-sm font-black text-slate-600" data-testid="profile-last-session-text">
            Última prática: {progress?.last_session_title || "nenhuma sessão ainda"}
          </p>
        </article>
  );
}

function ProfileForm({ form, mutation, notice, onSubmit, updateField }) {
  return (
    <form className="neo-card surface-neutral p-5" onSubmit={onSubmit} data-testid="profile-form">
          <h2 className="font-display text-3xl font-black" data-testid="profile-form-title">Dados do aluno</h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div>
              <label className="auth-label" htmlFor="profile-name-input" data-testid="profile-name-label">Nome</label>
              <input id="profile-name-input" className="auth-input" value={form.name} onChange={(event) => updateField("name", event.target.value)} required data-testid="profile-name-input" />
            </div>
            <div>
              <label className="auth-label" htmlFor="profile-phone-input" data-testid="profile-phone-label">Telefone</label>
              <input id="profile-phone-input" className="auth-input" value={form.phone} onChange={(event) => updateField("phone", event.target.value)} data-testid="profile-phone-input" />
            </div>
            <div>
              <label className="auth-label" htmlFor="profile-level-display" data-testid="profile-level-label">Nível definido pelo teste</label>
              <div id="profile-level-display" className="auth-input flex items-center" data-testid="profile-level-display">{form.level}</div>
            </div>
            <div>
              <label className="auth-label" htmlFor="profile-goals-input" data-testid="profile-goals-label">Objetivos</label>
              <input id="profile-goals-input" className="auth-input" value={form.goals} onChange={(event) => updateField("goals", event.target.value)} data-testid="profile-goals-input" />
            </div>
          </div>
          {notice && <div className="auth-error mt-4" role="status" data-testid="profile-save-alert">{notice}</div>}
          <button className="primary-action mt-5" type="submit" disabled={mutation.isPending} data-testid="profile-save-button">
            {mutation.isPending ? "Salvando..." : "Salvar perfil"} <Save size={18} />
          </button>
        </form>
  );
}

function GrammarProgress({ grammarModules }) {
  return (
    <section className="mt-8" data-testid="profile-grammar-section">
        <div className="mb-5 flex items-center gap-3">
          <BookOpenCheck size={28} />
          <div>
            <p className="section-kicker" data-testid="profile-grammar-kicker">gramática</p>
            <h2 className="font-display text-3xl font-black" data-testid="profile-grammar-title">Progresso por módulo</h2>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2" data-testid="profile-grammar-module-grid">
          {grammarModules.map((item) => (
            <article className="neo-card surface-neutral p-5" key={item.module.id} data-testid={`profile-grammar-module-${item.module.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-black uppercase text-slate-500" data-testid={`profile-grammar-module-level-${item.module.id}`}>{item.module.level}</p>
                  <h3 className="font-display text-2xl font-black" data-testid={`profile-grammar-module-title-${item.module.id}`}>{item.module.title}</h3>
                </div>
                <span className="profile-badge" data-testid={`profile-grammar-module-percent-${item.module.id}`}>{item.progress.percent_complete}%</span>
              </div>
              <div className="grammar-progress-track mt-4" data-testid={`profile-grammar-module-track-${item.module.id}`}>
                <span style={{ width: `${item.progress.percent_complete}%` }} data-testid={`profile-grammar-module-fill-${item.module.id}`} />
              </div>
              <p className="mt-3 text-sm font-black text-slate-600" data-testid={`profile-grammar-module-detail-${item.module.id}`}>
                {item.progress.completed_lessons}/{item.progress.total_lessons} aulas concluídas · média {item.progress.best_average_score}%
              </p>
            </article>
          ))}
        </div>
      </section>
  );
}

function ReviewAnalyticsSection({ reviewAnalytics }) {
  return (
    <section className="mt-8" data-testid="profile-review-analytics-section">
        <div className="mb-5 flex items-center gap-3">
          <Brain size={28} />
          <div>
            <p className="section-kicker" data-testid="profile-review-kicker">revisão inteligente</p>
            <h2 className="font-display text-3xl font-black" data-testid="profile-review-title">Pontos para reforçar</h2>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-4" data-testid="profile-review-analytics-grid">
          <article className="neo-card surface-yellow p-5" data-testid="profile-review-due-card">
            <p className="text-xs font-black uppercase text-slate-500">Vencidos</p>
            <h3 className="font-display text-4xl font-black" data-testid="profile-review-due-value">{reviewAnalytics?.due_cards || 0}</h3>
          </article>
          <article className="neo-card surface-mint p-5" data-testid="profile-review-mastered-card">
            <p className="text-xs font-black uppercase text-slate-500">Dominados</p>
            <h3 className="font-display text-4xl font-black" data-testid="profile-review-mastered-value">{reviewAnalytics?.mastered_cards || 0}</h3>
          </article>
          <article className="neo-card surface-blue p-5" data-testid="profile-review-total-card">
            <p className="text-xs font-black uppercase text-slate-500">Total</p>
            <h3 className="font-display text-4xl font-black" data-testid="profile-review-total-value">{reviewAnalytics?.total_cards || 0}</h3>
          </article>
          <article className="neo-card surface-neutral p-5" data-testid="profile-review-strength-card">
            <p className="text-xs font-black uppercase text-slate-500">Força média</p>
            <h3 className="font-display text-4xl font-black" data-testid="profile-review-strength-value">{reviewAnalytics?.average_strength || 0}</h3>
          </article>
        </div>
        <p className="mt-4 text-sm font-black text-slate-600" data-testid="profile-review-weak-sources">
          Categorias fracas: {(reviewAnalytics?.weak_sources || []).join(", ") || "nenhuma ainda"}
        </p>
        <div className="neo-card surface-neutral mt-5 p-5" data-testid="profile-review-trend-card">
          <h3 className="font-display text-2xl font-black" data-testid="profile-review-trend-title">Tendência dos últimos 7 dias</h3>
          <div className="review-trend-grid" data-testid="profile-review-trend-grid">
            {(reviewAnalytics?.trend || []).map((point) => {
              const maxValue = Math.max(...(reviewAnalytics?.trend || []).flatMap((item) => [item.due, item.mastered]), 1);
              return (
                <div className="review-trend-column" key={point.label} data-testid={`profile-review-trend-${point.label.replace(/[^0-9]/g, "-")}`}>
                  <div className="review-trend-bars">
                    <span className="review-trend-bar-due" style={{ height: `${Math.max((point.due / maxValue) * 100, point.due ? 12 : 2)}%` }} data-testid={`profile-review-trend-due-${point.label.replace(/[^0-9]/g, "-")}`} />
                    <span className="review-trend-bar-mastered" style={{ height: `${Math.max((point.mastered / maxValue) * 100, point.mastered ? 12 : 2)}%` }} data-testid={`profile-review-trend-mastered-${point.label.replace(/[^0-9]/g, "-")}`} />
                  </div>
                  <small data-testid={`profile-review-trend-label-${point.label.replace(/[^0-9]/g, "-")}`}>{point.label}</small>
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-xs font-black uppercase text-slate-500" data-testid="profile-review-trend-legend">amarelo = vencidos · verde = dominados</p>
        </div>
      </section>
  );
}

export default function Profile() {
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState({ name: user.name, phone: user.phone || "", level: user.level || "Básico", goals: (user.goals || []).join(", ") });
  const [notice, setNotice] = useState("");
  const { data: progress } = useQuery({ queryKey: ["progress"], queryFn: api.getProgress });
  const { data: grammarModules = [] } = useQuery({ queryKey: ["grammar-modules"], queryFn: api.getGrammarModules });
  const { data: reviewAnalytics } = useQuery({ queryKey: ["review-analytics"], queryFn: api.getReviewAnalytics });
  const mutation = useMutation({
    mutationFn: (payload) => api.updateProfile(payload),
    onSuccess: (profile) => {
      updateUser(profile);
      setNotice("Perfil salvo com sucesso.");
    },
    onError: (error) => setNotice(formatApiErrorDetail(error.response?.data?.detail)),
  });
  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const handleSubmit = (event) => {
    event.preventDefault();
    mutation.mutate({ name: form.name, phone: form.phone, goals: form.goals.split(",").map((goal) => goal.trim()).filter(Boolean) });
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8" data-testid="profile-page">
      <ProfileHeader user={user} />
      <section className="mt-8 grid gap-6 lg:grid-cols-[0.9fr_1.1fr]" data-testid="profile-content-grid">
        <ProgressCard progress={progress} />
        <ProfileForm form={form} mutation={mutation} notice={notice} onSubmit={handleSubmit} updateField={updateField} />
      </section>
      <GrammarProgress grammarModules={grammarModules} />
      <ReviewAnalyticsSection reviewAnalytics={reviewAnalytics} />
    </div>
  );
}